import {
  compareAsc,
  differenceInYears,
  isValid as isDateValid,
} from 'date-fns';
import * as Yup from 'yup';
import {
  yupStringNumberValidator,
  yupZipShortValidator,
} from 'Admin/utils/formValidators';
import { isMissing } from 'Shared/utils';
import { FormikErrors, FormikTouched, getIn } from 'formik';
import { ServerErrorsType } from './contexts';

import { ClientFieldDataType } from '../services/endpoints/GetDetails';
import {
  DriverDataByFieldValueType,
  VehicleDataByFieldValueType,
} from './formik';
import { STATE_OPTIONS } from 'Shared/constants/states';

export const yupDOBValidator = () => {
  return Yup.string().test(
    'DOB',
    'Must be a valid date of birth',
    (value: string) =>
      isMissing(value) || // Only validate if a value is present
      value.includes('*') || // do not attempt to validate masked data
      (isDateValid(new Date(value)) &&
        differenceInYears(new Date(), new Date(value)) > 0 &&
        differenceInYears(new Date(), new Date(value)) <= 200 &&
        value.length === 10)
  );
};

export const VIN_REGEX = /^[0-9A-HJ-NPR-Za-hj-npr-z]{17}$/i;
export const normalizeVin = (vin: string) => vin.replace(/\s/g, '');
export const yupVinValidator = () => {
  return Yup.string()
    .length(17, 'VIN must be exactly 17 characters')
    .matches(
      VIN_REGEX,
      'VINs do not include the letters O, I, and Q to avoid confusion with numbers 0, 1, and 9'
    );
};

type ValueOf<T> = T[keyof T];

/**
 * https://github.com/jquense/yup/issues/130#issuecomment-578392176
 * this function allows us to map incidentValidation to any incident key that we get
 * @param keys: the keys that will fill an our object schema
 * @param yupSchema: the yup validation that is applied as the values in our object schema
 */
const reduceKeysToObjectOfYupSchema = (
  keys: Array<string>,
  yupSchema: Yup.ObjectSchema<any>
) => keys.reduce((acc, key) => ({ ...acc, [key]: yupSchema }), {});

const months = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

const getAccidentValidation = (
  selfReportedAccidentsForClassificationsFlag: boolean = false,
  collectDayOfAccidentFlag: boolean = false
) => {
  return Yup.object()
    .shape(
      {
        month: Yup.string().when('year', {
          is: (year) => !!year,
          then: Yup.string().required('Required'),
        }),
        day: collectDayOfAccidentFlag
          ? Yup.string().required('Required')
          : Yup.string(),
        year: Yup.string().when('month', {
          is: (month) => !!month,
          then: Yup.string().required('Required'),
        }),
        typeOfLoss: selfReportedAccidentsForClassificationsFlag
          ? Yup.string().required('Required')
          : Yup.string(),
      },
      /**
       * https://github.com/jquense/yup/issues/79#issuecomment-699605408
       * this prevents cyclical dependency errors
       */
      [['month', 'year']]
    )
    .test('Past date', 'Error', function () {
      return Object.values(this.parent)
        .filter(({ month, year }: any) => month && year)
        .every(({ month, year }: any) => {
          const m = months.indexOf(month);
          const y = parseInt(year);
          return new Date(y, m) < new Date();
        });
    });
};

const incidentValidation = Yup.object()
  .shape(
    {
      month: Yup.string().when('year', {
        is: (year) => !!year,
        then: Yup.string().required('Required'),
      }),
      year: Yup.string().when('month', {
        is: (month) => !!month,
        then: Yup.string().required('Required'),
      }),
    },
    /**
     * https://github.com/jquense/yup/issues/79#issuecomment-699605408
     * this prevents cyclical dependency errors
     */
    [['month', 'year']]
  )
  .test('Past date', 'Error', function () {
    return Object.values(this.parent)
      .filter(({ month, year }: any) => month && year)
      .every(({ month, year }: any) => {
        const m = months.indexOf(month);
        const y = parseInt(year);
        return new Date(y, m) < new Date();
      });
  });

export const driverValidation = (
  driver: ValueOf<DriverDataByFieldValueType<ClientFieldDataType['value']>>,
  selfReportedAccidentsForClassificationsFlag: boolean = false,
  collectDayOfAccidentFlag: boolean = false
) =>
  Yup.object().shape({
    driverType: Yup.string(),
    dateOfBirth: yupDOBValidator(),
    ageFirstLicensed: Yup.string().when('excludeFromQuote', {
      is: (excludeFromQuote) => excludeFromQuote === 'false',
      then: yupStringNumberValidator(),
    }),
    hasCompletedDrivingCourse: Yup.string(),
    drivingCourseDate: Yup.string().when(
      ['excludeFromQuote', 'hasCompletedDrivingCourse'],
      {
        is: (excludeFromQuote, hasCompletedDrivingCourse) =>
          excludeFromQuote === 'false' && hasCompletedDrivingCourse === 'true',
        then: Yup.string().test(
          'drivingCourseDate',
          'Must be a valid date',
          (value) =>
            isMissing(value) || // Only validate if a value is present
            (isDateValid(new Date(value)) &&
              compareAsc(new Date(), new Date(value)) >= 0)
        ),
      }
    ),
    violations: Yup.object(
      reduceKeysToObjectOfYupSchema(
        Object.keys(driver.violations),
        incidentValidation
      )
    ),
    accidents: Yup.object(
      reduceKeysToObjectOfYupSchema(
        Object.keys(driver.accidents),
        getAccidentValidation(
          selfReportedAccidentsForClassificationsFlag,
          collectDayOfAccidentFlag
        )
      )
    ),
  });

export const vehicleValidation = Yup.object().shape({
  vehicleServicesId: Yup.string(),
  annualMileage: Yup.string().matches(
    /^[0-9,]*$/,
    'Must be a valid annual mileage'
  ),
  vin: yupVinValidator(),
  lienHolder: Yup.object().shape({
    postalCode: yupZipShortValidator(), // validating for short zip only, see AGT-2207
  }),
  garagedAtPrimaryAddress: Yup.string(),
  garagingAddress: Yup.object().when('garagedAtPrimaryAddress', {
    is: 'false',
    then: Yup.object({
      addressLine1: Yup.string().required('Street address is required'),
      city: Yup.string().required('City is required'),
      state: Yup.string().oneOf(Object.keys(STATE_OPTIONS)),
      postalCode: yupZipShortValidator().required('ZIP Code is required'),
    }),
    otherwise: null,
  }),
});

export const contactInfoValidation = (driverId: string) =>
  Yup.object().shape({
    [driverId]: Yup.object().shape({
      email: Yup.string().email('Must be a valid email'),
      phoneNumber: Yup.string()
        .transform((value: string) => value.replace(/[^0-9.]/g, ''))
        .min(10, 'Must be 10 numeric digits'),
    }),
  });

export const addressValidation = (driverId: string) =>
  Yup.object().shape({
    [driverId]: Yup.object().shape({
      address1: Yup.string().required('Street address is required'),
      address2: Yup.string(),
      city: Yup.string().required('City is required'),
      state: Yup.string()
        .oneOf(Object.keys(STATE_OPTIONS))
        .required('State is required'),
      zip: yupZipShortValidator().required('ZIP Code is required'),
    }),
  });

// TODO: This can be improved by making a useFieldErrors hook that can use the Formik context
export function getFieldErrors<T>(
  fieldName: string,
  required: boolean,
  alwaysShowErrors: boolean,
  errors: FormikErrors<T>,
  serverErrors: ServerErrorsType,
  values: T,
  touched: FormikTouched<T>,
  initialValues?: T
) {
  // Get the validation error, if any
  const validationError = getIn(errors, fieldName);

  // Get the required error, if applicable
  const requiredError =
    alwaysShowErrors &&
    required &&
    (!getIn(values, fieldName) || getIn(values, fieldName) === 'unknown')
      ? 'Required'
      : undefined;

  // Check if there's a server error when a value exists
  const serverFieldErrors = getIn(serverErrors, fieldName);
  const hasServerError =
    getIn(values, fieldName) &&
    serverFieldErrors &&
    serverFieldErrors.length > 0;
  const hasValueChanged =
    getIn(values, fieldName) !== getIn(initialValues, fieldName);
  const serverError =
    !getIn(touched, fieldName) && !hasValueChanged && hasServerError
      ? !getIn(values, fieldName) || getIn(values, fieldName) === 'unknown'
        ? 'Required'
        : 'Invalid data'
      : undefined; // remove error after touched

  // If the error should be shown, return the appropriate error, with preference to validation errors
  const feError =
    alwaysShowErrors || getIn(touched, fieldName)
      ? validationError || requiredError
      : undefined;

  return feError || serverError;
}

export const getVehicleError = (
  vehicle: VehicleDataByFieldValueType<string>[0],
  isEditingMultipleVehicles = false
): string | null => {
  if (isMissingVehicleInfo(vehicle)) {
    return 'Please select a Year/Make/Model/Body Style, or enter a Vehicle Identification Number (VIN) in order to continue.';
  }

  if (isUnverifiedVin(vehicle)) {
    if (isEditingMultipleVehicles) {
      return "Required: Please select the vehicle that matches the customer's VIN";
    } else {
      return 'Unverified VIN. Click Lookup VIN to continue.';
    }
  }

  return null;
};

export const isMissingVehicleInfo = (
  vehicle: VehicleDataByFieldValueType<string>[0]
) => {
  return (
    vehicle &&
    vehicle.vin === '' &&
    (vehicle.year === '' ||
      vehicle.make === '' ||
      vehicle.model === '' ||
      vehicle.bodyStyle === '')
  );
};

export const isUnverifiedVin = (
  vehicle: VehicleDataByFieldValueType<string>[0]
) => {
  return vehicle && vehicle.vin !== '' && vehicle.vehicleServicesId === '';
};
